import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { Market } from '../../../../models/MarketsModels';

export interface TimeZoneConversionRow {
  id: string;
  marketName: string;
  timezoneInfo: string;
  startTime: string;
  endTime: string;
}

export interface TimeZoneConversionHeader {
  id: string;
  label: string;
}

interface UseTimeZoneConversionTableProps {
  baseTimezone: string;
  startDate: DateTime | null;
  endDate: DateTime | null;
  markets: Market[];
}

function useTimeZoneConversionTable({ baseTimezone, startDate, endDate, markets }: UseTimeZoneConversionTableProps) {
  const [t] = useTranslation();

  /**
   * Convert a DateTime to a target timezone and format it
   * @param dateTime
   * @param targetTimezone
   */
  const convertDateTime = (dateTime: DateTime | null, targetTimezone: string) => {
    if (!dateTime) return '—';
    const anchored = dateTime.setZone(baseTimezone, { keepLocalTime: true });
    const converted = anchored.setZone(targetTimezone);
    return converted.toFormat('dd/MM/yyyy, HH:mm');
  };

  /**
   * Get the timezone label with abbreviation and UTC offset for a given timezone
   * @param timezone
   */
  const getTimezoneInfo = (timezone: string) => {
    const reference = DateTime.now().setZone(timezone);
    return `${timezone} (${reference.offsetNameShort}, UTC${reference.toFormat('Z')})`;
  };

  const headers: TimeZoneConversionHeader[] = useMemo(
    () => [
      { id: 'market', label: t('pages.bannerOnboarding.schedule.table.market') },
      { id: 'startTime', label: t('pages.bannerOnboarding.schedule.table.startDate') },
      { id: 'endTime', label: t('pages.bannerOnboarding.schedule.table.endDate') },
    ],
    [t],
  );

  const rows: TimeZoneConversionRow[] = useMemo(
    () =>
      markets.map((market) => ({
        id: `${market.countryCode}-${market.timezone}`,
        marketName: market.name,
        timezoneInfo: getTimezoneInfo(market.timezone),
        startTime: convertDateTime(startDate, market.timezone),
        endTime: convertDateTime(endDate, market.timezone),
      })),
    [markets, startDate, endDate, baseTimezone],
  );

  return { headers, rows };
}

export default useTimeZoneConversionTable;
